import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Image as ImageIcon, Info, CheckCircle, Calendar, MapPin, Clock, Users, Euro, ArrowRight, ArrowLeft, Map as MapIcon, ListChecks, Phone, Mail, Globe, Instagram, Languages, Car, Accessibility, HelpCircle } from 'lucide-react';
import Logo from '@/components/Logo';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MultiSelect from '@/components/ui/MultiSelect';

// Fix for Leaflet marker icons in React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Mock existing listings for duplicate detection
const existingListings = [
  { businessName: "Sauna Paradise", address: "123 Main St" },
  { businessName: "Barrel Sauna", address: "Lakeside" },
  { businessName: "Cabin Sauna", address: "Beachside" },
  { businessName: "Tent Sauna", address: "Forest" },
  { businessName: "Infrared Sauna", address: "Urban / Rooftop" },
];

const steps = [
  'Business & Location',
  'Photos',
  'Details & Amenities',
  'Booking & Pricing',
  'Preview & Publish',
];

const DUMMY_IMAGE = 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=400&q=80';
const DUMMY_MAP = 'https://maps.googleapis.com/maps/api/staticmap?center=53.3498,-6.2603&zoom=12&size=400x200&key=AIzaSyDUMMYKEY';

const daysOfWeek = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

const amenitiesList = [
  'Towels', 'Showers', 'Parking', 'Cold Plunge', 'Steam Room', 'Private Rooms', 'Lockers', 'WiFi', 'Sauna Hats', 'Refreshments'
];
const accessibilityList = [
  'Wheelchair Accessible', 'Step-free Entrance', 'Accessible Toilet', 'Assistance Available'
];

// Matrix options
const typeOptions = [
  'Barrel Sauna', 'Cabin Sauna', 'Tent Sauna', 'Infrared Sauna', 'Electric Sauna', 'Smoke Sauna', 'Mobile Sauna'
];
const heatSourceOptions = [
  'Wood-Fired', 'Electric', 'Infrared', 'Smoke (Chimneyless)', 'Gas'
];
const locationContextOptions = [
  'Lakeside', 'Beachside', 'Forest', 'Urban / Rooftop', 'Garden / Private Home', 'Mobile to hire'
];
const accessTypeOptions = [
  'Private', 'Shared / Public', 'Bookable for Events', 'Drop-in Sessions', 'Hire & Tow'
];
const experienceTypeOptions = [
  'Cold Plunge', 'Shower / Wash Area', 'Changing Rooms', 'Relaxation Area', 'Toilet Access', 'Towels / Robes Included', 'Shacks / Drinks', 'Firepit / Chill Zone'
];
const operatorTypeOptions = [
  'Independent Owner', 'Spa / Hotel', 'Wellness Studio', 'Festival / Event Vendor', 'Community Group'
];
const BOOKING_PAYMENT_OPTIONS = [
  { label: 'Walk-Ins', value: 'Walk-Ins' },
  { label: 'Phone Booking', value: 'Phone Booking' },
  { label: 'Email Booking', value: 'Email Booking' },
  { label: 'Advance Payment Required', value: 'Advance Payment Required' },
  { label: 'Cash Payments', value: 'Cash Payments' },
  { label: 'Card Payments', value: 'Card Payments' },
  { label: 'Group Bookings', value: 'Group Bookings' },
  { label: 'Online Booking', value: 'Online Booking' },
];

const checklist = [
  'Business/location info complete',
  'Photos uploaded',
  'Details & amenities set',
  'Booking & pricing configured',
  'All required fields complete',
];

const labelClass = "block font-semibold text-sauna-navy mb-1";
const inputClass = "input input-bordered w-full rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-sauna-orange focus:border-sauna-orange transition";
const textareaClass = "input input-bordered w-full rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-sauna-orange focus:border-sauna-orange transition min-h-[80px]";

const wizardSteps = [
  'Sauna Overview',
  'Business & Contact Info',
  'Photos',
  'Amenities & Operations',
  'Booking & Pricing',
  'Review & Publish',
];

const LOCAL_STORAGE_KEY = 'saunadip-wizard-draft';

// Static country/county center data for demo
const COUNTRY_CENTERS: Record<string, { lat: number, lng: number }> = {
  ireland: { lat: 53.3498, lng: -6.2603 },
  uk: { lat: 54.5, lng: -2.0 },
};
const COUNTY_CENTERS: Record<string, { lat: number, lng: number }> = {
  dublin: { lat: 53.3498, lng: -6.2603 },
  cork: { lat: 51.8985, lng: -8.4756 },
  galway: { lat: 53.2707, lng: -9.0568 },
};
const COUNTRY_OPTIONS = [
  { value: 'ireland', label: 'Ireland' },
  { value: 'uk', label: 'United Kingdom' },
];
const COUNTY_OPTIONS = [
  { value: 'dublin', label: 'Dublin' },
  { value: 'cork', label: 'Cork' },
  { value: 'galway', label: 'Galway' },
];

function MapPicker({ lat, lng, setLatLng, country, county }: { lat: number, lng: number, setLatLng: (lat: number, lng: number) => void, country: string, county: string }) {
  // Compute center
  let center = COUNTRY_CENTERS[country] || COUNTRY_CENTERS['ireland'];
  if (county && COUNTY_CENTERS[county]) center = COUNTY_CENTERS[county];
  // If user has picked a lat/lng, center on that
  if (lat && lng) center = { lat, lng };

  function DraggableMarker() {
    const [position, setPosition] = React.useState<[number, number]>([lat, lng]);
    const markerRef = React.useRef<any>(null);
    useMapEvents({
      click(e) {
        setPosition([e.latlng.lat, e.latlng.lng]);
        setLatLng(e.latlng.lat, e.latlng.lng);
      },
    });
    return (
      <Marker
        draggable
        eventHandlers={{
          dragend: () => {
            const marker = markerRef.current;
            if (marker) {
              const { lat, lng } = marker.getLatLng();
              setPosition([lat, lng]);
              setLatLng(lat, lng);
            }
          },
        }}
        position={position}
        ref={markerRef}
      />
    );
  }
  return (
    <MapContainer center={center} zoom={county ? 11 : 7} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <DraggableMarker />
    </MapContainer>
  );
}

const AMENITIES_OPTIONS = amenitiesList.map(a => ({ label: a, value: a }));
const ACCESSIBILITY_OPTIONS = accessibilityList.map(a => ({ label: a, value: a }));
const EXPERIENCE_TYPE_OPTIONS = experienceTypeOptions.map(a => ({ label: a, value: a }));

// Rename component
const SaunaWizardPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [photos, setPhotos] = useState<string[]>([]);
  // Duplicate/claim state
  const [claiming, setClaiming] = useState(false);
  const [ignoreDuplicate, setIgnoreDuplicate] = useState(false);
  const [claimEmail, setClaimEmail] = useState('');
  const [claimed, setClaimed] = useState(false);
  // Add plan selection state
  const [plan, setPlan] = useState<'FREE' | 'PREMIUM'>('FREE');
  // In component state:
  const [photoTypes, setPhotoTypes] = useState<string[]>([]);

  // --- Form State with Auto-Save ---
  const [form, setForm] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {
      businessName: '',
      type: '',
      heatSource: '',
      locationContext: '',
      accessType: '',
      venueFeatures: Array.isArray(saved?.venueFeatures) ? saved.venueFeatures : [],
      experienceAddons: Array.isArray(saved?.experienceAddons) ? saved.experienceAddons : [],
      customAddon: saved?.customAddon || '',
      operatorType: '',
      bookingPayment: [],
      address: '',
      city: '',
      state: '',
      zip: '',
      description: '',
      lat: '',
      lng: '',
      email: '',
      phone: '',
      website: '',
      instagram: '',
      hours: daysOfWeek.map(() => ({ open: true, from: '09:00', to: '17:00' })),
      amenities: [],
      accessibility: [],
      parkingType: '',
      parkingSpaces: '',
      parkingNotes: '',
      languages: '',
      specialRules: '',
      price: 25,
      duration: 60,
      guests: 4,
      instantBooking: true,
      buffer: 15,
      minNotice: 1,
      advance: 30,
      bookingLink: '',
      bookingInstructions: '',
      cancellationPolicy: '',
    };
  });
  useEffect(() => { localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(form)); }, [form]);

  // Duplicate check
  const isDuplicate = existingListings.some(
    l =>
      l.businessName.trim().toLowerCase() === form.businessName.trim().toLowerCase() &&
      l.address.trim().toLowerCase() === form.address.trim().toLowerCase()
  );

  // Handlers
  const handleAddSamplePhoto = () => setPhotos([...photos, DUMMY_IMAGE]);
  const handleRemovePhoto = (idx: number) => setPhotos(photos.filter((_, i) => i !== idx));
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox' && e.target instanceof HTMLInputElement && e.target.dataset.multi === 'true') {
      // Multi-select for experienceType or bookingPayment
      setForm((prev) => ({
        ...prev,
        [name]: prev[name].includes(value)
          ? prev[name].filter((v: string) => v !== value)
          : [...prev[name], value]
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };
  const handleHourChange = (idx: number, field: 'from' | 'to' | 'open', value: string | boolean) => {
    setForm((prev) => ({
      ...prev,
      hours: prev.hours.map((h, i) => i === idx ? { ...h, [field]: value } : h)
    }));
  };
  const handleSlider = (name: string, value: number) => setForm((prev) => ({ ...prev, [name]: value }));
  const handleToggle = (name: string) => setForm((prev) => ({ ...prev, [name]: !prev[name as keyof typeof prev] }));
  const handleAmenityToggle = (amenity: string) => {
    setForm((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };
  const handleAccessibilityToggle = (item: string) => {
    setForm((prev) => ({
      ...prev,
      accessibility: prev.accessibility.includes(item)
        ? prev.accessibility.filter(a => a !== item)
        : [...prev.accessibility, item]
    }));
  };

  // Step Content
  let content;
  if (currentStep === 0) {
    // Step 1: Sauna Overview
    content = (
      <div className="bg-white rounded-3xl shadow-xl p-0 max-w-5xl mx-auto border border-gray-200 flex flex-col">
        <div className="pt-12 pb-6 px-8 flex flex-col items-center text-center">
          <div className="flex justify-center items-center gap-3 mb-2">
            <ListChecks className="h-8 w-8 text-sauna-orange" />
            <h1 className="text-3xl font-extrabold text-sauna-navy">Sauna Overview</h1>
          </div>
          <p className="text-gray-500 text-lg">Tell us about your sauna setup and features</p>
        </div>
        <div className="px-8 pb-10 flex-1 flex flex-col justify-center">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* Sauna Details Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col gap-4 h-full min-h-[500px]">
              <div className="flex items-center gap-2 mb-2">
                <ListChecks className="h-5 w-5 text-sauna-orange" />
                <span className="font-bold text-sauna-navy text-lg">Sauna Details</span>
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <div>
                  <Label>
                    Sauna Type
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 cursor-pointer text-gray-400">ℹ️</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Select the type of sauna structure (e.g. barrel, cabin, tent, etc.)
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <select name="type" value={form.type} onChange={handleChange} className={inputClass + ' px-4 py-3 text-base'} required>
                    <option value="">Select type</option>
                    {typeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div>
                  <Label>
                    Heat Source
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 cursor-pointer text-gray-400">ℹ️</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Select the primary source of heat for your sauna (e.g. wood, electric, infrared, smoke)
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <select name="heatSource" value={form.heatSource} onChange={handleChange} className={inputClass + ' px-4 py-3 text-base'} required>
                    <option value="">Select heat source</option>
                    {heatSourceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div>
                  <Label>
                    Operator Type
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 cursor-pointer text-gray-400">ℹ️</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Select the primary role or entity managing the sauna (e.g. independent owner, spa, wellness studio)
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <select name="operatorType" value={form.operatorType} onChange={handleChange} className={inputClass + ' px-4 py-3 text-base'} required>
                    <option value="">Select operator type</option>
                    {operatorTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div>
                  <Label>
                    Languages Spoken
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 cursor-pointer text-gray-400">ℹ️</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        List the languages you can communicate in for guests.
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <input name="languages" value={form.languages} onChange={handleChange} placeholder="e.g. English, Irish, Polish" className={inputClass + ' px-4 py-3 text-base'} />
                </div>
              </div>
            </div>
            {/* Context & Features Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col gap-4 h-full min-h-[500px]">
              <div className="flex items-center gap-2 mb-2">
                <ListChecks className="h-5 w-5 text-sauna-orange" />
                <span className="font-bold text-sauna-navy text-lg">Context & Features</span>
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <div>
                  <Label>
                    Location Context
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 cursor-pointer text-gray-400">ℹ️</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Select the primary location where your sauna is situated (e.g. lakeside, beachside, forest, urban rooftop)
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <select name="locationContext" value={form.locationContext} onChange={handleChange} className={inputClass + ' px-4 py-3 text-base'} required>
                    <option value="">Select context</option>
                    {locationContextOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div>
                  <Label>
                    Access Type
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 cursor-pointer text-gray-400">ℹ️</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Select how guests can access your sauna (e.g. private, shared, bookable for events, drop-in sessions)
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <select name="accessType" value={form.accessType} onChange={handleChange} className={inputClass + ' px-4 py-3 text-base'} required>
                    <option value="">Select access type</option>
                    {accessTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div>
                  <Label>
                    Venue Features
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="ml-1 cursor-pointer text-gray-400">ℹ️</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Select the main features available at your venue (e.g. changing rooms, towels, firepit, etc.)
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <MultiSelect
                    options={[
                      { label: 'Changing Rooms', value: 'Changing Rooms' },
                      { label: 'Firepit / Chill Zone', value: 'Firepit / Chill Zone' },
                      { label: 'Relaxation Area', value: 'Relaxation Area' },
                      { label: 'Shower / Wash Area', value: 'Shower / Wash Area' },
                      { label: 'Toilet Access', value: 'Toilet Access' },
                      { label: 'Towels / Robes Included', value: 'Towels / Robes Included' },
                    ]}
                    value={form.venueFeatures}
                    onChange={selected => setForm(prev => ({ ...prev, venueFeatures: selected }))}
                    placeholder="Select venue features..."
                  />
                </div>
                <Label className="mt-4">
                  Extras / Experience Add-ons
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="ml-1 cursor-pointer text-gray-400">ℹ️</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Select any extra experiences or add-ons you offer (e.g. cold plunge, drinks, shacks, or add your own)
                    </TooltipContent>
                  </Tooltip>
                </Label>
                <MultiSelect
                  options={[
                    { label: 'Cold Plunge', value: 'Cold Plunge' },
                    { label: 'Drinks', value: 'Drinks' },
                    { label: 'Shacks', value: 'Shacks' },
                    { label: 'Other', value: 'Other' },
                  ]}
                  value={form.experienceAddons}
                  onChange={selected => setForm(prev => ({ ...prev, experienceAddons: selected }))}
                  placeholder="Select add-ons..."
                />
                {Array.isArray(form.experienceAddons) && form.experienceAddons.includes('Other') && (
                  <input
                    name="customAddon"
                    value={form.customAddon || ''}
                    onChange={e => setForm(prev => ({ ...prev, customAddon: e.target.value }))}
                    placeholder="Describe your custom add-on..."
                    className={inputClass + ' px-4 py-3 text-base mt-2'}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (currentStep === 1) {
    // Step 2: Business & Contact Info
    content = (
      <div className="bg-white rounded-3xl shadow-xl p-0 max-w-5xl mx-auto border border-gray-200 flex flex-col">
        <div className="pt-12 pb-6 px-8 flex flex-col items-center text-center">
          <div className="flex justify-center items-center gap-3 mb-2">
            <Mail className="h-8 w-8 text-sauna-orange" />
            <h1 className="text-3xl font-extrabold text-sauna-navy">Business & Contact Info</h1>
          </div>
          <p className="text-gray-500 text-lg">Let guests know who you are and how to reach you</p>
        </div>
        <div className="px-8 pb-10 flex-1 flex flex-col justify-center">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* Business & Contact Details Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col gap-4 h-full min-h-[600px]">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-5 w-5 text-sauna-orange" />
                <span className="font-bold text-sauna-navy text-lg">Business & Contact Details</span>
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">Business Name</label>
                  <input name="businessName" value={form.businessName} onChange={handleChange} placeholder="Business Name" className={inputClass + ' px-4 py-3 text-base'} required />
                </div>
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">Short Description</label>
                  <input name="description" value={form.description} onChange={handleChange} placeholder="Short Description" maxLength={200} className={inputClass + ' px-4 py-3 text-base'} />
                </div>
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">Email</label>
                  <input name="email" value={form.email} onChange={handleChange} placeholder="e.g. hello@saunadip.ie" className={inputClass + ' px-4 py-3 text-base'} />
                </div>
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">Phone</label>
                  <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone" className={inputClass + ' px-4 py-3 text-base'} />
                </div>
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">Website</label>
                  <input name="website" value={form.website} onChange={handleChange} placeholder="e.g. https://yoursite.com" className={inputClass + ' px-4 py-3 text-base'} />
                </div>
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">Instagram</label>
                  <input name="instagram" value={form.instagram} onChange={handleChange} placeholder="e.g. @yourhandle" className={inputClass + ' px-4 py-3 text-base'} />
                </div>
              </div>
            </div>
            {/* Location & Address Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col gap-4 h-full min-h-[600px]">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-5 w-5 text-sauna-orange" />
                <span className="font-bold text-sauna-navy text-lg">Location & Address</span>
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">Country</label>
                  <select name="country" value={form.country || 'ireland'} onChange={handleChange} className={inputClass + ' px-4 py-3 text-base'}>
                    {COUNTRY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">County</label>
                  <select name="county" value={form.county || ''} onChange={handleChange} className={inputClass + ' px-4 py-3 text-base'}>
                    <option value="">Select county</option>
                    {COUNTY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">Address</label>
                  <input name="address" value={form.address} onChange={handleChange} placeholder="Street Address" className={inputClass + ' px-4 py-3 text-base'} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-semibold text-sauna-navy mb-1">City</label>
                    <input name="city" value={form.city} onChange={handleChange} placeholder="City" className={inputClass + ' px-4 py-3 text-base'} />
                  </div>
                  <div>
                    <label className="block font-semibold text-sauna-navy mb-1">State</label>
                    <input name="state" value={form.state} onChange={handleChange} placeholder="State" className={inputClass + ' px-4 py-3 text-base'} />
                  </div>
                </div>
                {form.country === 'uk' && (
                  <div>
                    <label className="block font-semibold text-sauna-navy mb-1">ZIP Code (optional)</label>
                    <input name="zip" value={form.zip} onChange={handleChange} placeholder="ZIP Code" className={inputClass + ' px-4 py-3 text-base'} />
                  </div>
                )}
                {/* Map Picker */}
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">Map Location</label>
                  <div className="h-48 rounded-lg overflow-hidden mb-2">
                    <MapPicker
                      lat={parseFloat(form.lat) || COUNTRY_CENTERS[form.country || 'ireland'].lat}
                      lng={parseFloat(form.lng) || COUNTRY_CENTERS[form.country || 'ireland'].lng}
                      setLatLng={(lat, lng) => setForm((prev: any) => ({ ...prev, lat, lng }))}
                      country={form.country || 'ireland'}
                      county={form.county || ''}
                    />
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block font-semibold text-sauna-navy mb-1">Latitude</label>
                      <input name="lat" value={form.lat} onChange={handleChange} placeholder="e.g. 53.3498" className={inputClass + ' px-4 py-3 text-base'} />
                    </div>
                    <div className="flex-1">
                      <label className="block font-semibold text-sauna-navy mb-1">Longitude</label>
                      <input name="lng" value={form.lng} onChange={handleChange} placeholder="e.g. -6.2603" className={inputClass + ' px-4 py-3 text-base'} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (currentStep === 2) {
    // Step 3: Photos (Redesigned)
    content = (
      <div className="bg-white rounded-3xl shadow-xl p-0 max-w-5xl mx-auto border border-gray-200 flex flex-col">
        <div className="pt-12 pb-6 px-8 flex flex-col items-center text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <ImageIcon className="h-8 w-8 text-sauna-orange" />
            <h2 className="text-3xl font-extrabold text-sauna-navy">Upload Beautiful Photos</h2>
          </div>
          <p className="text-gray-500 text-lg">Showcase your sauna with stunning images that attract guests</p>
        </div>
        <div className="px-8 pb-10 flex-1 flex flex-col justify-center">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Left: Dropzone & Tips */}
            <div>
              <div className="bg-sauna-beige border border-sauna-orange/30 rounded-xl flex flex-col items-center justify-center p-8 mb-4 min-h-[260px]">
                <ImageIcon className="h-12 w-12 text-sauna-orange mb-4" />
                <div className="font-semibold text-sauna-navy mb-2 text-center">Drop photos here or click to upload</div>
                <div className="text-gray-500 text-sm mb-4 text-center">Support for JPG, PNG files up to 10MB each</div>
                <Button className="bg-sauna-orange text-white font-semibold px-6 py-2 rounded mb-2">+ Choose Photos</Button>
                <Button variant="outline" onClick={handleAddSamplePhoto} className="mb-2">Add Sample Photo (for testing)</Button>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mt-2">
                <div className="font-semibold text-sauna-orange mb-2">Photo Tips:</div>
                <ul className="text-sauna-orange text-sm list-disc ml-5 space-y-1">
                  <li>Include exterior and interior shots</li>
                  <li>Show the sauna in use (with permission)</li>
                  <li>Capture the ambiance and atmosphere</li>
                  <li>Use natural lighting when possible</li>
                </ul>
              </div>
            </div>
            {/* Right: Uploaded Photos Grid */}
            <div>
              <div className="mb-2 font-semibold text-sauna-navy">Uploaded Photos ({photos.length})</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative group">
                    <img src={photo} alt={`Photo ${idx + 1}`} className="rounded-xl w-full h-48 object-cover" />
                    <select
                      value={photoTypes[idx] || ''}
                      onChange={e => {
                        const newTypes = [...photoTypes];
                        newTypes[idx] = e.target.value;
                        setPhotoTypes(newTypes);
                      }}
                      className="absolute top-2 right-2 bg-white border border-gray-300 rounded px-2 py-1 text-xs shadow"
                    >
                      <option value="">Select type</option>
                      <option value="Interior">Interior</option>
                      <option value="Exterior">Exterior</option>
                      <option value="Action">Action</option>
                      <option value="Ambience">Ambience</option>
                      <option value="Venue">Venue</option>
                    </select>
                    <button onClick={() => handleRemovePhoto(idx)} className="absolute top-1 right-1 bg-white/80 rounded-full p-1 text-xs text-sauna-orange hover:bg-sauna-orange hover:text-white transition">&times;</button>
                  </div>
                ))}
                {/* Fill empty slots for consistent grid height */}
                {Array.from({ length: Math.max(0, 6 - photos.length) }).map((_, i) => (
                  <div key={i + photos.length} className="rounded-xl bg-sauna-orange/10 h-24" />
                ))}
              </div>
              {/* Progress Bar */}
              {photos.length > 0 && (
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                  <div className="bg-sauna-orange h-full transition-all" style={{ width: `${(photos.length / 6) * 100}%` }} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  } else if (currentStep === 3) {
    // Step 4: Amenities & Operations
    content = (
      <div className="bg-white rounded-3xl shadow-xl p-0 max-w-5xl mx-auto border border-gray-200 flex flex-col">
        <div className="pt-12 pb-6 px-8 flex flex-col items-center text-center">
          <div className="flex justify-center items-center gap-3 mb-2">
            <ListChecks className="h-8 w-8 text-sauna-orange" />
            <h1 className="text-3xl font-extrabold text-sauna-navy">Amenities & Operations</h1>
          </div>
          <p className="text-gray-500 text-lg">Tell us about your amenities, accessibility, and operations</p>
        </div>
        <div className="px-8 pb-10 flex-1 flex flex-col justify-center">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Services & Accessibility Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col gap-6 h-full min-h-[500px] w-full md:w-1/2">
              <div className="flex items-center gap-2 mb-2">
                <ListChecks className="h-5 w-5 text-sauna-orange" />
                <span className="font-bold text-sauna-navy text-lg">Services & Amenities</span>
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <MultiSelect
                  options={AMENITIES_OPTIONS}
                  value={form.amenities}
                  onChange={selected => setForm(prev => ({ ...prev, amenities: selected }))}
                  placeholder="Select amenities..."
                />
                <div className="mt-4">
                  <div className="font-semibold text-sauna-navy mb-2 flex items-center gap-2"><Accessibility className="h-5 w-5 text-sauna-orange" />Accessibility Features</div>
                  <MultiSelect
                    options={ACCESSIBILITY_OPTIONS}
                    value={form.accessibility}
                    onChange={selected => setForm(prev => ({ ...prev, accessibility: selected }))}
                    placeholder="Select accessibility features..."
                  />
                </div>
                <div className="mt-6">
                  <div className="font-semibold text-sauna-navy mb-2 flex items-center gap-2">
                    <Car className="h-5 w-5 text-sauna-orange" />Car Parking
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Parking Type</label>
                        <select
                          name="parkingType"
                          value={form.parkingType}
                          onChange={handleChange}
                          className={inputClass}
                        >
                          <option value="">Select type</option>
                          <option value="Street Parking">Street Parking</option>
                          <option value="Private Parking">Private Parking</option>
                          <option value="Public Parking">Public Parking</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Spaces</label>
                        <input
                          type="number"
                          name="parkingSpaces"
                          value={form.parkingSpaces}
                          onChange={handleChange}
                          min="0"
                          className={inputClass}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Parking Notes</label>
                      <textarea
                        name="parkingNotes"
                        value={form.parkingNotes}
                        onChange={handleChange}
                        placeholder="Additional parking information"
                        className={textareaClass}
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Operating Hours & Other Details Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col gap-6 h-full min-h-[500px] w-full md:w-1/2">
              <OperatingHoursCard
                hours={form.hours}
                onHourChange={handleHourChange}
                onCopyToAll={() => {
                  const monday = form.hours[0];
                  setForm((prev: any) => ({
                    ...prev,
                    hours: prev.hours.map((h: any, i: number) => i === 0 ? h : { ...h, open: monday.open, from: monday.from, to: monday.to })
                  }));
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  } else if (currentStep === 4) {
    // Step 5: Booking & Pricing (Redesigned)
    content = (
      <div className="bg-white rounded-3xl shadow-xl p-0 max-w-5xl mx-auto border border-gray-200 flex flex-col">
        <div className="pt-12 pb-6 px-8 flex flex-col items-center text-center">
          <div className="flex justify-center items-center gap-3 mb-2">
            <Calendar className="h-8 w-8 text-sauna-orange" />
            <h2 className="text-3xl font-extrabold text-sauna-navy">Setup Booking Calendar</h2>
          </div>
          <p className="text-gray-500 text-lg">Configure your pricing and availability settings</p>
        </div>
        <div className="px-8 pb-10 flex-1 flex flex-col justify-center">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block font-semibold text-sauna-navy mb-1">Booking Link</label>
              <input name="bookingLink" value={form.bookingLink} onChange={handleChange} placeholder="https://your-booking-system.com" className={inputClass + ' px-4 py-3 text-base'} />
            </div>
            <div>
              <label className="block font-semibold text-sauna-navy mb-1">Booking Instructions</label>
              <textarea name="bookingInstructions" value={form.bookingInstructions} onChange={handleChange} placeholder="How should guests book sessions? Include any special requirements or processes." className={textareaClass + ' px-4 py-3 text-base'} />
            </div>
          </div>
          <div className="mb-6">
            <label className="block font-semibold text-sauna-navy mb-1">Cancellation Policy</label>
            <textarea name="cancellationPolicy" value={form.cancellationPolicy} onChange={handleChange} placeholder="Describe your cancellation and refund policy" className={textareaClass + ' px-4 py-3 text-base'} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Pricing & Sessions Card */}
            <div className="bg-white border rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Euro className="h-6 w-6 text-sauna-orange" />
                <span className="font-bold text-lg text-sauna-navy">Pricing & Sessions</span>
              </div>
              <div className="mb-6">
                <label className="block font-semibold text-sauna-navy mb-1">Base Price per Session</label>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-500">{form.price}</span>
                </div>
                <input type="range" min={1} max={200} step={1} value={form.price} onChange={e => handleSlider('price', Number(e.target.value))} className="w-full accent-sauna-orange" />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1</span>
                  <span>200</span>
                </div>
              </div>
              <div className="mb-6">
                <label className="block font-semibold text-sauna-navy mb-1">Session Duration</label>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-500">{form.duration} minutes</span>
                </div>
                <input type="range" min={15} max={180} step={15} value={form.duration} onChange={e => handleSlider('duration', Number(e.target.value))} className="w-full accent-sauna-orange" />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>15 min</span>
                  <span>3 hours</span>
                </div>
              </div>
              <div>
                <label className="block font-semibold text-sauna-navy mb-1">Maximum Guests</label>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-500">{form.guests} {form.guests === 1 ? 'person' : 'people'}</span>
                </div>
                <input type="range" min={1} max={20} step={1} value={form.guests} onChange={e => handleSlider('guests', Number(e.target.value))} className="w-full accent-sauna-orange" />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1 person</span>
                  <span>20 people</span>
                </div>
              </div>
            </div>
            {/* Availability Settings Card */}
            <div className="bg-white border rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="h-6 w-6 text-sauna-orange" />
                <span className="font-bold text-lg text-sauna-navy">Availability Settings</span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <label className="block font-semibold text-sauna-navy mb-1">Instant Booking</label>
                  <span className="text-xs text-gray-500">Allow guests to book immediately</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={1}
                  value={form.instantBooking ? 1 : 0}
                  onChange={e => setForm(prev => ({ ...prev, instantBooking: e.target.value === '1' }))}
                  className="w-24 accent-sauna-orange ml-4 align-middle"
                />
                <span className={`ml-2 font-semibold ${form.instantBooking ? 'text-sauna-orange' : 'text-gray-400'}`}>{form.instantBooking ? 'On' : 'Off'}</span>
              </div>
              <div className="mb-4">
                <label className="block font-semibold text-sauna-navy mb-1">Buffer Time Between Sessions</label>
                <div className="flex items-center gap-2">
                  <input type="number" min={0} max={120} value={form.buffer} onChange={e => handleSlider('buffer', Number(e.target.value))} className="w-20 px-3 py-2 border rounded focus:ring-2 focus:ring-sauna-orange" />
                  <span className="text-sm text-gray-500">minutes</span>
                </div>
              </div>
              <div className="mb-4">
                <label className="block font-semibold text-sauna-navy mb-1">Minimum Booking Notice</label>
                <div className="flex items-center gap-2">
                  <input type="number" min={0} max={48} value={form.minNotice} onChange={e => handleSlider('minNotice', Number(e.target.value))} className="w-20 px-3 py-2 border rounded focus:ring-2 focus:ring-sauna-orange" />
                  <span className="text-sm text-gray-500">hours in advance</span>
                </div>
              </div>
              <div>
                <label className="block font-semibold text-sauna-navy mb-1">How far in advance can guests book?</label>
                <div className="flex items-center gap-2">
                  <input type="number" min={1} max={365} value={form.advance} onChange={e => handleSlider('advance', Number(e.target.value))} className="w-20 px-3 py-2 border rounded focus:ring-2 focus:ring-sauna-orange" />
                  <span className="text-sm text-gray-500">days</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-8">
            <label className="block font-semibold text-sauna-navy mb-1">Booking & Payment Options</label>
            <MultiSelect
              options={BOOKING_PAYMENT_OPTIONS}
              value={form.bookingPayment}
              onChange={selected => setForm(prev => ({ ...prev, bookingPayment: selected }))}
              placeholder="Select booking/payment options..."
            />
          </div>
          {/* Booking Summary */}
          <div className="bg-orange-50 border border-orange-100 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Euro className="h-5 w-5 text-sauna-orange" />
              <span className="font-semibold text-sauna-navy">{form.price} per {form.duration}min session</span>
            </div>
            <div className="flex items-center gap-4">
              <Users className="h-5 w-5 text-sauna-orange" />
              <span className="font-semibold text-sauna-navy">Up to {form.guests} guests</span>
            </div>
            <div className="flex items-center gap-4">
              <Clock className="h-5 w-5 text-sauna-orange" />
              <span className="font-semibold text-sauna-navy">{form.buffer}min buffer time</span>
            </div>
          </div>
          <div className="mt-6">
            <label className="block font-semibold text-sauna-navy mb-1">Special Rules / Notes</label>
            <textarea name="specialRules" value={form.specialRules} onChange={handleChange} placeholder="e.g. No children under 12, bring your own towel, etc." className={textareaClass + ' px-4 py-3 text-base'} />
          </div>
        </div>
      </div>
    );
  } else if (currentStep === 5) {
    // Step 6: Review & Publish (Plan selection and preview only)
    content = (
      <div className="bg-white rounded-3xl shadow-xl p-0 max-w-5xl mx-auto border border-gray-200 flex flex-col">
        <div className="pt-12 pb-6 px-8 flex flex-col items-center text-center">
          <div className="flex justify-center items-center gap-3 mb-2">
            <CheckCircle className="h-8 w-8 text-sauna-orange" />
            <h2 className="text-3xl font-extrabold text-sauna-navy">Preview & Publish</h2>
          </div>
          <p className="text-gray-500 text-lg">Review your listing and choose your plan before publishing</p>
        </div>
        <div className="px-8 pb-10 flex-1 flex flex-col justify-center">
          {/* Plan Selection */}
          <div className="flex gap-6 mb-6 justify-center">
            <button
              className={`px-6 py-3 rounded-lg border-2 font-bold text-lg transition-all duration-150 ${plan === 'FREE' ? 'border-sauna-orange bg-sauna-orange/10 text-sauna-orange' : 'border-gray-200 bg-white text-gray-500 hover:border-sauna-orange'}`}
              onClick={() => setPlan('FREE')}
            >
              Free
            </button>
            <button
              className={`px-6 py-3 rounded-lg border-2 font-bold text-lg transition-all duration-150 ${plan === 'PREMIUM' ? 'border-sauna-orange bg-sauna-orange text-white' : 'border-gray-200 bg-white text-gray-500 hover:border-sauna-orange'}`}
              onClick={() => setPlan('PREMIUM')}
            >
              Premium
            </button>
          </div>
          {/* Preview Card */}
          <div className="bg-sauna-beige rounded-lg border border-gray-200 p-6 shadow space-y-4 w-full">
            <div className="flex items-center gap-4 mb-4">
              <img src={photos[0] || DUMMY_IMAGE} alt="Preview" className="w-20 h-20 object-cover rounded shadow" />
              <div>
                <div className="text-xl font-bold text-sauna-navy">{form.businessName || 'Your Sauna Name'}</div>
                <div className="text-gray-500">{form.address}, {form.city}, {form.state}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Users className="h-4 w-4 text-sauna-orange" />
                  <span>{form.guests} guests</span>
                  <Clock className="h-4 w-4 text-sauna-orange" />
                  <span>{form.duration} min</span>
                  <Euro className="h-4 w-4 text-sauna-orange" />
                  <span>{form.price}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.amenities.map(a => (
                    <span key={a} className={`px-2 py-1 rounded text-xs ${plan === 'FREE' && !['Towels', 'Parking', 'Showers'].includes(a) ? 'bg-gray-200 text-gray-400 line-through' : 'bg-sauna-orange/10 text-sauna-orange'}`}>{a}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="text-gray-700 mb-2">{form.description}</div>
            <div className="text-gray-600 text-sm mb-2">Contact: {form.email} {form.phone && `| ${form.phone}`}</div>
            <div className="text-gray-600 text-sm mb-2">Website: {form.website} {form.instagram && `| IG: ${form.instagram}`}</div>
            <div className="text-gray-600 text-sm mb-2">Languages: {form.languages}</div>
            <div className="text-gray-600 text-sm mb-2">
              Parking: {form.parkingType}
              {form.parkingSpaces && ` | ${form.parkingSpaces} spaces`}
              {form.parkingNotes && <div className="mt-1 text-gray-500">{form.parkingNotes}</div>}
            </div>
            <div className="text-gray-600 text-sm mb-2">Accessibility: {form.accessibility.join(', ')}</div>
            <div className="text-gray-600 text-sm mb-2">Special Rules: {form.specialRules}</div>
            {plan === 'PREMIUM' && (
              <div className="mb-4"><span className="bg-sauna-orange text-white px-3 py-1 rounded-full font-bold text-sm">PREMIUM</span> <span className="text-sauna-orange font-semibold ml-2">All features enabled</span></div>
            )}
            {plan === 'FREE' && (
              <div className="mb-4"><span className="bg-gray-200 text-sauna-navy px-3 py-1 rounded-full font-bold text-sm">FREE</span> <span className="text-gray-500 font-semibold ml-2">Some features are only available on Premium</span></div>
            )}
            <Button className={`w-full mt-4 ${plan === 'FREE' ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-sauna-orange text-white'}`} disabled={plan === 'FREE'}>
              {plan === 'FREE' ? 'Upgrade to Premium to Publish' : 'Publish Listing'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Progress Bar
  const progress = ((currentStep + 1) / wizardSteps.length) * 100;

  // --- Operating Hours Section ---
  function OperatingHoursCard({ hours, onHourChange, onCopyToAll }: { hours: any[], onHourChange: any, onCopyToAll: any }) {
    return (
      <div className="bg-white rounded-2xl p-6 flex flex-col gap-4">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5 text-sauna-orange" />
          <span className="font-bold text-sauna-navy text-lg">Operating Hours</span>
        </div>
        <div className="divide-y divide-gray-100">
          {daysOfWeek.map((day, idx) => (
            <div key={day} className="py-4 first:pt-0 last:pb-0">
              <div className="grid grid-cols-2 items-center mb-2">
                <div className="flex items-center gap-3">
                  <span className="w-28 font-medium text-gray-700">{day}</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hours[idx].open}
                      onChange={e => onHourChange(idx, 'open', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-sauna-orange"></div>
                    <div className="absolute left-[2px] top-[2px] w-5 h-5 bg-white rounded-full transition-all peer-checked:translate-x-5"></div>
                  </label>
                  <span className="text-gray-500">{hours[idx].open ? 'Open' : 'Closed'}</span>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => onCopyToAll()}
                    className="text-sm text-sauna-orange hover:text-orange-600 transition"
                  >
                    Copy to all days
                  </button>
                </div>
              </div>
              {hours[idx].open && (
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Open:</label>
                    <input
                      type="time"
                      value={hours[idx].from}
                      onChange={e => onHourChange(idx, 'from', e.target.value)}
                      className="w-32 px-3 py-1.5 border rounded text-base focus:ring-2 focus:ring-sauna-orange"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Close:</label>
                    <input
                      type="time"
                      value={hours[idx].to}
                      onChange={e => onHourChange(idx, 'to', e.target.value)}
                      className="w-32 px-3 py-1.5 border rounded text-base focus:ring-2 focus:ring-sauna-orange"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0] py-4">
      <div className="w-full max-w-4xl mx-auto pt-0 pb-2">
        <div className="flex items-center justify-between w-full mb-2">
          {wizardSteps.map((label, idx) => (
            <button
              key={label}
              type="button"
              onClick={() => setCurrentStep(idx)}
              className="flex flex-col items-center flex-1 focus:outline-none transition bg-transparent border-none"
              style={{ cursor: 'pointer' }}
            >
              <div className={`w-9 h-9 flex items-center justify-center rounded-full border-2 mb-1 text-base font-bold transition-all duration-200
                ${idx === currentStep
                  ? 'bg-sauna-orange text-white border-sauna-orange shadow'
                  : 'bg-white border-sauna-orange/40 text-sauna-orange'}`}>{idx + 1}</div>
              <span className={`text-xs mt-1 text-center truncate ${idx === currentStep ? 'text-sauna-orange font-semibold' : 'text-gray-400'}`}>{label}</span>
            </button>
          ))}
        </div>
        {/* Progress Bar */}
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-300" style={{ width: `${progress}%`, background: '#FF7A00' }} />
        </div>
      </div>
      <div className="w-full max-w-4xl mx-auto mb-24">
        <div className="bg-white rounded-3xl shadow-xl p-0 max-w-5xl mx-auto border border-gray-200 min-h-[600px]">
          {content}
        </div>
      </div>
      <footer className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 shadow z-50 py-4 px-4 flex justify-center">
        <div className="w-full max-w-4xl flex justify-between items-center">
          {currentStep < wizardSteps.length - 1 ? (
            <>
              <Button
                onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
                disabled={currentStep <= 0}
                variant="outline"
                className="flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                onClick={() => setCurrentStep((s) => Math.min(wizardSteps.length - 1, s + 1))}
                className="bg-sauna-orange text-white flex items-center px-8 py-3 text-lg rounded-xl shadow"
              >
                Continue <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          ) : (
            <Button
              className={`w-full max-w-xs py-4 text-lg font-bold rounded-xl shadow ${plan ? 'bg-sauna-orange text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              disabled={!plan}
              onClick={() => {
                window.location.href = '/dashboard?success=sauna-live';
              }}
            >
              Complete & Publish Live
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
};

export default SaunaWizardPage; 